type StorageType = 'localStorage' | 'sessionStorage';

export interface SafeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
  keys(): string[];
}

const memoryFallback: Record<StorageType, Record<string, string>> = {
  localStorage: {},
  sessionStorage: {},
};

const cache: Partial<Record<StorageType, SafeStorage>> = {};

function getNativeStorage(type: StorageType): Storage | null {
  try {
    const storage = window[type];
    const probe = '__chekin_storage_probe__';
    storage.setItem(probe, probe);
    storage.removeItem(probe);
    return storage;
  } catch {
    return null;
  }
}

function buildSafeStorage(type: StorageType): SafeStorage {
  const native = getNativeStorage(type);
  const memory = memoryFallback[type];

  if (native) {
    return {
      getItem: key => {
        try {
          return native.getItem(key);
        } catch {
          return memory[key] ?? null;
        }
      },
      setItem: (key, value) => {
        try {
          native.setItem(key, value);
        } catch {
          memory[key] = value;
        }
      },
      removeItem: key => {
        try {
          native.removeItem(key);
        } catch {
          delete memory[key];
        }
      },
      clear: () => {
        try {
          native.clear();
        } catch {
          /* noop */
        }
      },
      keys: () => {
        try {
          return Object.keys(native);
        } catch {
          return Object.keys(memory);
        }
      },
    };
  }

  return {
    getItem: key => (key in memory ? memory[key] : null),
    setItem: (key, value) => {
      memory[key] = value;
    },
    removeItem: key => {
      delete memory[key];
    },
    clear: () => {
      Object.keys(memory).forEach(key => delete memory[key]);
    },
    keys: () => Object.keys(memory),
  };
}

export function getSafeStorage(type: StorageType): SafeStorage {
  if (!cache[type]) {
    cache[type] = buildSafeStorage(type);
  }

  return cache[type] as SafeStorage;
}

export function isStorageAvailable(type: StorageType): boolean {
  return getNativeStorage(type) !== null;
}
